import CustomToolTip from 'components/CustomToolTip';
import moment from 'moment';
import React, { useState } from 'react';

interface AccrualScheduleItem {
  cycle: number;
  label?: string;
  eligibleDate: string;
  hsaAmount: number;
  lsaAmount: number;
  status: 'earned' | 'future' | 'inactive' | 'inactive_dr';
}

interface AccrualDetailsCardProps {
  totalYearlyBenefit?: number;
  accrualFrequency?: string;
  periods?: number;
  amountPerPeriod?: number;
  accrualSchedule?: AccrualScheduleItem[];
  type: 'HSA' | 'LSA';
  nextAccrualDate?: string | null;
  currentEligibleDate?: string | null;
}

export default function AccrualDetailsCard({
  totalYearlyBenefit = 0,
  accrualFrequency = 'N/A',
  amountPerPeriod = 0,
  accrualSchedule = [],
  type,
  nextAccrualDate = null,
  currentEligibleDate = null,
}: AccrualDetailsCardProps) {

  const [hoveredAmount, setHoveredAmount] = useState<string | null>(null);
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({
    visibility: 'hidden',
    position: 'absolute',
    background: 'black',
    color: 'white',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    whiteSpace: 'nowrap',
    pointerEvents: 'none',
    zIndex: 10,
    left: '50%',
    transform: 'translateX(-50%)',
    bottom: 'calc(100% + 5px)',
    top: 'auto',
  });

  const getAmount = (item: AccrualScheduleItem): number =>
    type === 'HSA' ? (item.hsaAmount ?? 0) : (item.lsaAmount ?? 0);
  
  const handleMouseEnter = (e: React.MouseEvent<HTMLDivElement>, item: AccrualScheduleItem) => {
    const target = e.currentTarget;
    const amount = getAmount(item);
    
    if ((item.status === 'inactive' || item.status === 'inactive_dr') && amount === 0) {
        return;
    }

    const amountText = `$${amount.toFixed(2)}`;
    setHoveredAmount(amountText);

    const tooltipPositioningContext = target.closest('.accrual-container');

    if (tooltipPositioningContext) {
      const contextRect = tooltipPositioningContext.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      const targetCenterXinViewport = targetRect.left + (targetRect.width / 2);
      const leftPx = targetCenterXinViewport - contextRect.left;
      const bottomPx = contextRect.bottom - (targetRect.top - 5);

      setTooltipStyle(prev => ({
        ...prev,
        visibility: 'visible',
        left: `${leftPx}px`,
        top: 'auto',
        bottom: `${bottomPx}px`,
      }));
    }
  };

  const handleMouseLeave = () => {
    setHoveredAmount(null);
    setTooltipStyle(prev => ({ ...prev, visibility: 'hidden' }));
  };

  const legendSquareStyle: React.CSSProperties = {
    display: 'inline-block',
    width: '15px',
    height: '15px',
    borderRadius: '3px',
    marginRight: '6px',
    verticalAlign: 'middle',
  };

  const hasInactiveDR = accrualSchedule.some(item => item.status === 'inactive_dr');

  return (
    <div style={{ color: 'black' }}>
      <div>
        <p>
          {accrualSchedule.some(s => s.status === 'inactive' || s.status === 'inactive_dr')
            ? <>
                Your total <strong>prorated benefit of ${totalYearlyBenefit.toFixed(2)}</strong> for this year is being earned{' '}
                <strong>{accrualFrequency}</strong>.
              </>
            : <>
                Your total annual benefit of <strong>${totalYearlyBenefit.toFixed(2)}</strong> is being earned{' '}
                <strong>{accrualFrequency}</strong>.
              </>
          }
        </p>
      </div>

      <div style={{ position: 'relative' }} className="mt-3 accrual-container">
        <div className="row text-center mb-3">
          <div className="col">
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '4px' }}>
              {nextAccrualDate ? "Next Increment Date" : "Last Increment Date"}
              <CustomToolTip
                header={nextAccrualDate ? "Next Increment Date" : "Last Increment Date"}
                bodyContent="Amounts are earned on the 1st day of the month the increment begins with."
              />
            </div>
            <strong>
              {nextAccrualDate
                ? moment(nextAccrualDate).format("MMMM D, YYYY")
                : moment(currentEligibleDate).format("MMMM D, YYYY")}
            </strong>
          </div>
          <div className="col">
            <div>Amount earned per {accrualFrequency} period</div>
            <strong>${amountPerPeriod.toFixed(2)}</strong>
          </div>
        </div>

        {accrualSchedule.length > 0 ? (
          <div className="mb-2">
            {(() => {
              const renderSquare = (item: AccrualScheduleItem) => {
                let style: React.CSSProperties = {
                  flex: 1,
                  cursor: getAmount(item) > 0 || item.status !== 'inactive' ? 'pointer' : 'default',
                  borderRadius: '4px',
                  aspectRatio: '1 / 1',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.8rem',
                  maxHeight: '35px',
                };

                switch (item.status) {
                  case 'earned':
                    style.backgroundColor = '#198754';
                    style.color = 'white';
                    break;
                  case 'future':
                    style.backgroundColor = '#c0c0c0';
                    style.color = 'white';
                    break;
                  case 'inactive_dr':
                    style.backgroundColor = '#e8e8e8';
                    style.border = '1px solid rgb(0, 0, 0)';
                    style.color = 'black';
                    style.backgroundImage = 'repeating-linear-gradient(135deg, #6666668c 0, #6666668c 2px, transparent 2px, transparent 6px)';
                    style.backgroundSize = '100% 100%';
                    break;
                }

                return (
                  <div
                    key={item.cycle}
                    style={style}
                    onMouseEnter={(e) => handleMouseEnter(e, item)}
                  >
                    {item.label || ''}
                  </div>
                );
              };

              const filteredSchedule = accrualSchedule.filter(item => {
                if (item.status !== 'inactive') return true;
              });

              const midpoint = Math.ceil(filteredSchedule.length / 2);
              const firstRowItems = filteredSchedule.slice(0, midpoint);
              const secondRowItems = filteredSchedule.slice(midpoint);

              return (
                <div onMouseLeave={handleMouseLeave}>
                  <div style={{ display: 'flex', gap: '4px' }} className="mb-2">
                    {firstRowItems.map(item => renderSquare(item))}
                  </div>

                  {secondRowItems.length > 0 && (
                    <div style={{ display: 'flex', gap: '4px' }}>
                      {secondRowItems.map(item => renderSquare(item))}
                    </div>
                  )}
                </div>
              );
            })()}

            {hoveredAmount && <div style={tooltipStyle}>{hoveredAmount}</div>}

            <div className="d-flex justify-content-around mt-3 small">
              <span style={{ fontSize: '12px' }}>
                <span
                  style={{
                    ...legendSquareStyle,
                    backgroundColor: '#198754',
                  }}
                />
                Earned
              </span>
              <span style={{ fontSize: '12px' }}>
                <span
                  style={{
                    ...legendSquareStyle,
                    backgroundColor: '#c0c0c0',
                  }}
                />
                Next Credit
              </span>
              {hasInactiveDR && (
              <span style={{ fontSize: '12px' }}>
                <span
                  style={{
                    ...legendSquareStyle,
                    backgroundColor: '#e8e8e8',
                    backgroundImage:
                      'repeating-linear-gradient(135deg, #6666668c 0, #6666668c 2px, transparent 2px, transparent 6px)',
                    backgroundSize: '100% 100%',
                    border: '1px solid black',
                  }}
                />
                Inactive
              </span>
            )}
            </div>

          </div>
        ) : (
          <p className="text-muted small">Accrual schedule details not available.</p>
        )}
      </div>
    </div>
  );
}